import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Heart, Award, Brain } from 'lucide-react';

export const WealthSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useTranslation();

  return (
    <section ref={ref} className="py-20 lg:py-28 section-cream relative overflow-hidden">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="order-2 lg:order-1 relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-elevated">
              <img
                src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800&q=80"
                alt="Senior professional woman"
                className="w-full h-[450px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-accent/30 via-transparent to-transparent" />
            </div>

            {/* Floating Stats Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="absolute -bottom-6 -right-6 md:right-6 bg-card rounded-xl p-5 shadow-medium max-w-xs"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <Brain className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <div className="text-2xl font-serif font-bold text-foreground">30+</div>
                  <div className="text-sm text-muted-foreground">{t('wealth.avgExperience')}</div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="order-1 lg:order-2 space-y-6"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full">
              <Heart size={14} className="text-primary" />
              <span className="text-xs font-medium text-primary uppercase tracking-wider">
                {t('wealth.badge')}
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground">
              {t('wealth.title')}
            </h2>

            <p className="text-muted-foreground leading-relaxed">
              {t('wealth.description')}
            </p>

            <div className="space-y-4 pt-4">
              <div className="flex items-start gap-4 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/30 transition-colors">
                <div className="icon-circle shrink-0">
                  <Award size={20} />
                </div>
                <div>
                  <div className="font-medium text-foreground">{t('wealth.experience.title')}</div>
                  <div className="text-sm text-muted-foreground">
                    {t('wealth.experience.description')}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/30 transition-colors">
                <div className="icon-circle shrink-0">
                  <Brain size={20} />
                </div>
                <div>
                  <div className="font-medium text-foreground">{t('wealth.vision.title')}</div>
                  <div className="text-sm text-muted-foreground">
                    {t('wealth.vision.description')}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
